/**
 * Owner: Masa Bayu
 * File: landing-alerts.ts
 * Date:Time: 2026-10-18 11:42
 * Description: Bell notifikasi home §376 — stor localStorage sebenar (corak
 *   sample-access-store). Pengeluar: pesanan checkout dicipta. Kosong = kosong,
 *   tiada notifikasi rekaan.
 */

export const LANDING_ALERTS_STORAGE_KEY = 'qxread.landing-alerts.v1';

export interface LandingAlert {
  readonly id: string;
  readonly text: string;
  readonly at: number;
  readonly read: boolean;
}

const MAX_ALERTS = 20;
const EVENT_NAME = 'qxread:landing-alerts';

function sanitize(input: unknown): LandingAlert[] {
  if (!Array.isArray(input)) {
    return [];
  }
  const out: LandingAlert[] = [];
  for (const entry of input) {
    if (typeof entry !== 'object' || entry === null) {
      continue;
    }
    const item = entry as Record<string, unknown>;
    if (typeof item.id !== 'string' || typeof item.text !== 'string') {
      continue;
    }
    out.push({
      id: item.id,
      text: item.text,
      at: typeof item.at === 'number' ? item.at : 0,
      read: item.read === true,
    });
  }
  return out;
}

function readAlerts(): LandingAlert[] {
  try {
    const raw = window.localStorage.getItem(LANDING_ALERTS_STORAGE_KEY);
    if (raw === null) {
      return [];
    }
    return sanitize(JSON.parse(raw));
  } catch {
    return [];
  }
}

function writeAlerts(alerts: readonly LandingAlert[]): void {
  try {
    window.localStorage.setItem(LANDING_ALERTS_STORAGE_KEY, JSON.stringify(alerts));
  } catch {
    // Storage penuh/ditolak — kekal dalam memori sesi ini.
  }
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

export function listLandingAlerts(): readonly LandingAlert[] {
  return readAlerts().sort((a, b) => b.at - a.at);
}

export function unreadLandingAlertCount(): number {
  return readAlerts().filter((alert) => !alert.read).length;
}

export function pushLandingAlert(text: string): void {
  const id = `alert-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const next: LandingAlert = { id, text, at: Date.now(), read: false };
  writeAlerts([next, ...readAlerts()].slice(0, MAX_ALERTS));
}

export function markLandingAlertsRead(): void {
  writeAlerts(readAlerts().map((alert) => ({ ...alert, read: true })));
}

export function subscribeLandingAlerts(listener: () => void): () => void {
  const onStorage = (event: StorageEvent): void => {
    if (event.key === LANDING_ALERTS_STORAGE_KEY) {
      listener();
    }
  };
  const onLocal = (): void => listener();
  window.addEventListener('storage', onStorage);
  window.addEventListener(EVENT_NAME, onLocal);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(EVENT_NAME, onLocal);
  };
}
